import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { Tournament, Round } from '@swiss-magic/shared-types';

// Story 082 — current round panel. Picks the "current" round —
// last paired or last published (whichever is later in the list).
// Once the tournament is completed there's no live round; we show
// a summary instead.

interface Props {
  tournament: Tournament;
  rounds: Round[] | null;
  /** Story 093 — generating pairings is owner-only. */
  isOwner: boolean;
}

function pickCurrentRound(rounds: Round[]): Round | null {
  if (rounds.length === 0) return null;
  // Rounds are returned in ascending order. The most recently
  // generated one is the highest-numbered; that's what the director
  // is actively working on, regardless of its lifecycle status.
  return rounds[rounds.length - 1];
}

function pendingCount(round: Round): number {
  return round.pairings.filter((p) => p.resultStatus !== 'confirmed').length;
}

export function CurrentRoundPanel({ tournament, rounds, isOwner }: Props) {
  const { t } = useTranslation('director');
  const navigate = useNavigate();
  const status = tournament.status;
  const current = rounds ? pickCurrentRound(rounds) : null;
  const completedTotal = rounds?.length ?? 0;

  return (
    <Card data-testid="tournament-hub-panel-current-round">
      <CardHeader>
        <CardTitle className="text-base">
          {t('tournament_hub.panels.current_round.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!current && status === 'registration_closed' && isOwner && (
          <>
            <div
              data-testid="tournament-hub-generate-pairings-hint"
              className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
            >
              {t('tournament_hub.panels.current_round.generate_pairings_hint')}
            </div>
            <Button
              size="sm"
              data-testid="tournament-hub-generate-pairings-button"
              onClick={() => navigate(`/tournaments/${tournament.id}/rounds`)}
            >
              {t('tournament_hub.panels.current_round.generate_pairings')}
            </Button>
          </>
        )}
        {!current && !(status === 'registration_closed' && isOwner) && (
          <div
            data-testid="tournament-hub-no-rounds-empty"
            className="text-sm text-muted-foreground"
          >
            {t('tournament_hub.panels.current_round.no_rounds')}
          </div>
        )}
        {current && (
          <>
            <div
              data-testid="tournament-hub-current-round-info"
              className="text-sm"
            >
              {t('tournament_hub.panels.current_round.round_n', {
                n: current.roundNumber,
                status: current.status,
              })}
            </div>
            {current.pairings.length > 0 && current.status !== 'completed' && (
              <div className="text-xs text-muted-foreground">
                {t('tournament_hub.panels.current_round.results_x_of_y', {
                  x: current.pairings.length - pendingCount(current),
                  y: current.pairings.length,
                })}
              </div>
            )}
            {current.status === 'paired' && (
              <div
                data-testid="tournament-hub-publish-round-hint"
                className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
              >
                {t('tournament_hub.panels.current_round.publish_to_enter_results')}
              </div>
            )}
            {status === 'completed' ? (
              <Button
                size="sm"
                variant="outline"
                data-testid="tournament-hub-view-rounds-button"
                onClick={() => navigate(`/tournaments/${tournament.id}/rounds`)}
              >
                {t('tournament_hub.panels.current_round.view_rounds', {
                  total: completedTotal,
                })}
              </Button>
            ) : (
              <Button
                size="sm"
                data-testid="tournament-hub-go-to-round-button"
                onClick={() => navigate(`/tournaments/${tournament.id}/rounds`)}
              >
                {t('tournament_hub.panels.current_round.go_to_round')}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
